/**
 * Shared pure helper functions and constants for review workflows.
 */

// Review item status definitions (internalStatus / clientStatus on review items)
export const REVIEW_ITEM_STATUS = {
  PENDING_REVIEW: 'pending_review',
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'changes_requested',
  IN_REVISION: 'in_revision',
  PENDING_FINALIZATION: 'pending_finalization',
}

// Finalization status definitions (finalizationStatus on review items)
export const FINALIZATION_STATUS = {
  PENDING: 'pending',
  COMPLETE: 'complete',
}

/**
 * Fixed slot layout for podcast (primary-episode) review deliverables. Instead
 * of a free-form asset list, episode reviews must fill these named slots.
 * `target` tells the slot UI where the slot saves:
 *   - 'item'    → a review item doc carrying a matching `slot` field
 *   - 'project' → the episode project doc (thumbnail / title / show notes)
 * ponytail: fixed at 4 social cuts; bump the array if the count ever changes.
 */
export const PODCAST_REVIEW_SLOTS = [
  { key: 'full_episode', label: 'Full Episode', target: 'item', mediaType: 'video' },
  { key: 'social_1', label: 'Social Cut 1', target: 'item', mediaType: 'video' },
  { key: 'social_2', label: 'Social Cut 2', target: 'item', mediaType: 'video' },
  { key: 'social_3', label: 'Social Cut 3', target: 'item', mediaType: 'video' },
  { key: 'social_4', label: 'Social Cut 4', target: 'item', mediaType: 'video' },
  // Thumbnail is a reviewable image item (client can comment/approve); the newest
  // version's Bunny path is mirrored to project.episodeThumbnailPath for display.
  { key: 'thumbnail', label: 'Thumbnail', target: 'item', mediaType: 'image' },
  { key: 'episode_meta', label: 'Title & Show Notes', target: 'project', mediaType: 'text' },
]

/**
 * The slots that actually apply to a given episode. Title & Show Notes only exists
 * because Anthem writes the listing copy when Anthem distributes the show — when the
 * client publishes it themselves, that slot isn't ours to fill and is dropped
 * everywhere (admin sidebar, client rail, and both completeness gates).
 * `anthemDistributes` comes off the deliverable, copied down from the show setting.
 */
export function getApplicableEpisodeSlots({ anthemDistributes = true } = {}) {
  if (anthemDistributes !== false) return PODCAST_REVIEW_SLOTS
  return PODCAST_REVIEW_SLOTS.filter((s) => s.key !== 'episode_meta')
}

// Optional extra assets an admin adds beyond the fixed slots. Each gets a unique
// `extra_<ts>` slot key so every one-item-per-slot lookup keeps working. Once added
// they gate exactly like a fixed slot; they just never read as "missing" when absent.
export const EXTRA_SLOT_PREFIX = 'extra_'
export const isExtraSlot = (key) => typeof key === 'string' && key.startsWith(EXTRA_SLOT_PREFIX)

// The slot keys that map to review item docs (the five video buckets).
export const PODCAST_ITEM_SLOT_KEYS = PODCAST_REVIEW_SLOTS
  .filter((s) => s.target === 'item')
  .map((s) => s.key)

/**
 * Name of the per-slot "changes requested" flag mirrored onto the deliverable,
 * e.g. clientReviewSlotChanges_thumbnail. Flat booleans, not a map: automation
 * conditions read `deliverable[field]` and triggers diff TOP-LEVEL keys, so a
 * nested map would be invisible to both.
 */
function slotChangeField(prefix, slotKey) {
  return `${prefix}SlotChanges_${slotKey}`
}

/**
 * Per-slot revision flags for a slotted (podcast episode) review.
 * `items` = the review's item docs, `statusField` = internalStatus|clientStatus,
 * `prefix` = internalReview|clientReview. Slots with no item read false.
 */
export function computeSlotChangeFlags(items = [], statusField = 'clientStatus', prefix = 'clientReview') {
  const flags = {}
  for (const key of PODCAST_ITEM_SLOT_KEYS) flags[slotChangeField(prefix, key)] = false
  for (const item of items) {
    if (!item?.slot || !PODCAST_ITEM_SLOT_KEYS.includes(item.slot)) continue
    flags[slotChangeField(prefix, item.slot)] = item[statusField] === REVIEW_ITEM_STATUS.CHANGES_REQUESTED
  }
  return flags
}

/**
 * Slot-completeness for a primary-episode review, used to gate client review.
 * `items` = the review's item docs (each may carry a `slot` key);
 * `project` = the episode project doc (episodeTitle/episodeDescription/
 * episodeThumbnailUrl live there). Returns the per-slot filled map, the list of
 * missing slot keys, and an overall `isComplete`.
 *
 * `requireApproved` (used by the client-review gate): a video slot only counts
 * when its item is internally approved, not merely uploaded. Without it (the
 * sidebar display), presence is enough.
 * `requireClientApproved` (used by the client-side go-live gate): a video slot
 * counts only when the CLIENT has approved it (clientStatus). Takes precedence
 * over `requireApproved`.
 * `clientProvidesThumbnail`: the thumbnail is supplied by the client and lives
 * only on the project doc (no review item, no approval). It never blocks the
 * handoff INTO client review — the client can only upload it once the episode is
 * already there (EpisodeReviewPanel), so gating on it deadlocks. It counts only
 * at the go-live gate (`requireClientApproved`), and only when Anthem is the one
 * distributing; when the client publishes their own show, it gates nothing.
 */
export function getEpisodeSlotCompleteness(items = [], project = {}, {
  requireApproved = false,
  requireClientApproved = false,
  clientProvidesThumbnail = false,
  anthemDistributes = true,
} = {}) {
  const bySlot = {}
  for (const it of items) {
    if (it?.slot) bySlot[it.slot] = it
  }
  // Has this review already been handed to the client once? A promoted version is
  // the only durable trace. When the client approves an item the server copies the
  // asset into their library and deletes the review item (copyApprovedItemToAssets
  // in functions/reviews/index.js), so a slot they already signed off on reads as
  // empty from then on.
  const wasDelivered = items.some(
    (it) => (it?.versions || []).some((v) => v?.clientVersionNumber != null)
  )
  const itemOk = (slot) => {
    const it = bySlot[slot]
    // An EMPTY slot blocks the FIRST handoff to the client and nothing after it.
    //  · entry gate (requireApproved): on a revision round the client has already
    //    approved and cleared whatever isn't here, so re-gating on it deadlocks the
    //    episode in internal review — that is the only reason wasDelivered exists.
    //  · go-live gate (requireClientApproved): never blocks. Episodes stamped by
    //    backfillEpisodeSlots while already in client review can be a cut short, and
    //    waiting on an asset that will never exist locks Set Publish Date forever.
    // ponytail: delivered-and-cleared is indistinguishable from an item an admin
    // deleted on purpose. If that ever matters, stamp the slot onto the deliverable
    // in copyApprovedItemToAssets and read that instead of inferring.
    if (!it) return requireClientApproved || (requireApproved && wasDelivered)
    if (requireClientApproved) return it.clientStatus === REVIEW_ITEM_STATUS.APPROVED
    return requireApproved ? it.internalStatus === REVIEW_ITEM_STATUS.APPROVED : true
  }
  // A client-supplied thumbnail is only ever a blocker at the go-live gate, and
  // only when Anthem distributes — see the docblock above.
  const clientThumbGates = requireClientApproved && anthemDistributes !== false
  const filled = {}
  for (const key of PODCAST_ITEM_SLOT_KEYS) filled[key] = itemOk(key)
  if (clientProvidesThumbnail) {
    // Lives on the project doc, no review item, no approval step. Admin uploads via
    // EpisodeMetadataPanel store the path and clear the URL, so accept either.
    filled.thumbnail =
      !clientThumbGates || !!(project.episodeThumbnailUrl || project.episodeThumbnailPath)
  }
  // episode_meta stays a project-doc check (title + show notes, not a reviewed asset).
  filled.episode_meta = !!(project.episodeTitle && project.episodeDescription)
  const extraKeys = Object.keys(bySlot).filter(isExtraSlot)
  for (const key of extraKeys) filled[key] = itemOk(key)
  // Slots that don't apply to this episode can't be "missing" — see
  // getApplicableEpisodeSlots (Title & Show Notes drops when Anthem doesn't distribute).
  const missing = [...getApplicableEpisodeSlots({ anthemDistributes }).map((s) => s.key), ...extraKeys]
    .filter((k) => !filled[k])
  return { filled, missing, isComplete: missing.length === 0 }
}

/**
 * Returns true when the item still needs client action (i.e. hasn't been
 * approved, sent back for changes, or queued for finalization).
 */
export function isActionableStatus(clientStatus) {
  return (
    clientStatus !== REVIEW_ITEM_STATUS.APPROVED &&
    clientStatus !== REVIEW_ITEM_STATUS.CHANGES_REQUESTED &&
    clientStatus !== REVIEW_ITEM_STATUS.PENDING_FINALIZATION
  )
}

/**
 * Format seconds into a timecode string. Shows hours only when the value
 * reaches an hour (e.g. "3:45", "1:05:30") so long podcasts read cleanly
 * while short clips stay compact. Returns '' for null/undefined and '0:00'
 * for non-finite/negative values (duration can be briefly NaN/Infinity before
 * media metadata resolves).
 */
/**
 * Display name for a comment author. Users without a first/last name are
 * stored as their email — show just the part before the @.
 */
export function commentAuthorName(userName) {
  if (!userName) return 'Unknown'
  const name = String(userName).trim()
  return /^\S+@\S+$/.test(name) ? name.split('@')[0] : name
}

export function formatTimestamp(seconds) {
  if (seconds == null) return ''
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * Normalize a Firestore Timestamp / Date / ISO string / millis into epoch ms.
 * Returns 0 when the value is missing or unparseable (sorts oldest). Used to
 * keep a sortable creation time alongside the human-readable createdAt string.
 */
export function toMillis(timestamp) {
  if (!timestamp) return 0
  if (typeof timestamp?.toMillis === 'function') return timestamp.toMillis()
  if (typeof timestamp?.toDate === 'function') return timestamp.toDate().getTime()
  const ms = new Date(timestamp).getTime()
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * Format a Firestore timestamp (or Date-like value) into a human-readable
 * relative time string such as "Just now", "5 min ago", "2 days ago", etc.
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return ''
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Extract initials from a display name. Returns up to two uppercase characters.
 */
export function getInitials(name) {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

/**
 * Deterministically map a string to one of a fixed set of colours.
 */
export function stringToColor(str) {
  if (!str) return '#7a7a7a'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colors = ['#667eea', '#f5576c', '#48c78e', '#485fc7', '#ff6b6b', '#ffd93d', '#6c5ce7', '#00b894']
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Convert a hex colour to an rgba() string with low opacity, suitable for
 * light background tinting.
 */
function hexToLightBg(hex) {
  if (!hex) return 'transparent'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, 0.08)`
}

/**
 * Build CSS custom-property style object for a comment thread, using the
 * comment's avatarColor.
 */
export function commentStyle(comment) {
  return {
    '--comment-color': comment.avatarColor,
    '--comment-color-light': hexToLightBg(comment.avatarColor),
  }
}
